import * as routes from './logoBuilderRoutes';
import joinUrl from 'url-join';
import queryString from 'query-string';
import { stateToUrlParams } from './logoStateHelpers';
export var ARENA_NAVIGATION_SOURCE;
(function (ARENA_NAVIGATION_SOURCE) {
    ARENA_NAVIGATION_SOURCE["SPLIT_PAGE"] = "SPLIT_PAGE";
    ARENA_NAVIGATION_SOURCE["TWELFTH_PAGE_BANNER"] = "TWELFTH_PAGE_BANNER";
    ARENA_NAVIGATION_SOURCE["MARKETING_LP_HEADER"] = "MARKETING_LP_HEADER";
    ARENA_NAVIGATION_SOURCE["USER_PROFILE_MENU"] = "USER_PROFILE_MENU";
    ARENA_NAVIGATION_SOURCE["EDITOR_TEMPLATE_PANEL_BANNER"] = "EDITOR_TEMPLATE_PANEL_BANNER";
    ARENA_NAVIGATION_SOURCE["BUSINESS_MANAGER_WELCOME"] = "BUSINESS_MANAGER_WELCOME";
})(ARENA_NAVIGATION_SOURCE || (ARENA_NAVIGATION_SOURCE = {}));
const ArenaNavigationParam = {
    [ARENA_NAVIGATION_SOURCE.SPLIT_PAGE]: { entry: 'logo-maker-wiz', origin: 'split-page', serviceType: '5', innerType: '6' },
    [ARENA_NAVIGATION_SOURCE.TWELFTH_PAGE_BANNER]: { entry: 'logo-maker-wiz', origin: 'pop-up', serviceType: '5', innerType: '6' },
    [ARENA_NAVIGATION_SOURCE.MARKETING_LP_HEADER]: { entry: 'logo-maker-lp', origin: 'top-header', serviceType: '1', innerType: '1' },
    [ARENA_NAVIGATION_SOURCE.USER_PROFILE_MENU]: { entry: 'logo-maker-wiz', origin: 'account-menu', serviceType: '1', innerType: '1' },
    [ARENA_NAVIGATION_SOURCE.EDITOR_TEMPLATE_PANEL_BANNER]: { entry: 'logo-editor', origin: 'pop-up', serviceType: '5', innerType: '6' },
    [ARENA_NAVIGATION_SOURCE.BUSINESS_MANAGER_WELCOME]: { entry: 'bm-marketing-logo-maker', origin: 'empty-state', serviceType: '5', innerType: '6' }
};
const ARENA_BASED_URL = 'https://www.wix.com/marketplace/brief/match';
export class LogoUrlGenerator {
    constructor({ protocol, host, mountPoint, debugApp, isMobile = false, inBizMgr = false }) {
        this.protocol = protocol;
        this.host = host;
        this.mountPoint = mountPoint;
        this.debugApp = debugApp;
        this.isMobile = isMobile;
        this.inBizMgr = inBizMgr;
    }
    _createParam(name, value) {
        return `${name}=${encodeURIComponent(value)}`;
    }
    _createSearchParams(arrKeyValuePairs = []) {
        const searchParams = [];
        arrKeyValuePairs.forEach(keyValue => {
            const { key, value } = keyValue;
            searchParams.push(this._createParam(key, value));
        });
        if (this.debugApp) {
            searchParams.push(this._createParam('debugApp', this.debugApp));
        }
        return searchParams.length ? `?${searchParams.join('&')}` : '';
    }
    _getBaseMount() {
        return `${this.protocol}//${this.host}${this.mountPoint}`;
    }
    createUrl(path = '', params = {}, options = { isRelative: undefined }) {
        const isRelative = !!options.isRelative;
        let basePath = '/';
        if (!isRelative) {
            basePath = this._getBaseMount();
        }
        let paramsString = queryString.stringify(params);
        if (paramsString.length) {
            paramsString = '?' + paramsString;
        }
        return joinUrl(basePath, path, paramsString);
    }
    getHomePageUrl(arrKeyValuePairs = []) {
        return this._getBaseMount() + this._createSearchParams(arrKeyValuePairs);
    }
    getAIChatBotPageUrl() {
        return 'https://manage.wix.com/logo/maker/ai-assistant';
    }
    getMarketingHomePageUrl() {
        return 'https://www.wix.com/logo/maker';
    }
    getIndustryOnboardingUrl(arrKeyValuePairs = []) {
        const searchParams = this._createSearchParams(arrKeyValuePairs);
        return joinUrl(this._getBaseMount(), routes.INDUSTRY, searchParams);
    }
    getBrandStudioUrl(logoId, origin) {
        return `${this._getBaseMount()}${routes.DASHBOARD_BRAND_STUDIO}?logoId=${logoId}${origin ? `&referralAdditionalInfo=${origin}` : ''}`;
    }
    getPrintThankyouUrl() {
        return `${this._getBaseMount()}${routes.PRINTS_THANKYOU}`;
    }
    getPrintProductsUrl() {
        return `${this._getBaseMount()}${routes.PRINTS_PRODUCTS}`;
    }
    getPrintCheckoutUrl() {
        return `${this._getBaseMount()}${routes.PRINTS_CHECKOUT}`;
    }
    getLogoPrintUrl2(logoId, referralAdditionalInfo, editType = '', options) {
        const { printSuffix, printStaging } = options;
        const debugApp = this.debugApp;
        const params = {
            logoId,
            printSuffix,
            debugApp
        };
        if (printStaging && this.debugApp && this.debugApp.indexOf('printStaging') === -1) {
            params.debugApp = this.debugApp.replace('mockPurchase', 'printStaging|mockPurchase');
        }
        return this.createUrl(joinUrl(routes.PRINTS_EDITOR, editType), { ...params, referralAdditionalInfo }, options);
    }
    getLogoEditorUrl(selectedZoeTemplate, companyDetails, referral) {
        const urlParams = stateToUrlParams({ selectedZoeTemplate, companyDetails }, this.debugApp, referral);
        return joinUrl(this._getBaseMount(), routes.EDIT_LOGO, urlParams);
    }
    getLogoOnboardingUrl(companyDetails, industry, tags, referral) {
        const urlParams = stateToUrlParams({ companyDetails, industry, tags }, this.debugApp, referral);
        return joinUrl(this._getBaseMount(), routes.COMPANY_DETAILS, urlParams);
    }
    getPremiumDomainsUrl(domainName = '', referral = '') {
        return `https://apps.wix.com/redirect-post-domain/?domainName=${domainName.trim()}&referralAdditionalInfo=${referral}`;
    }
    getNewWebsiteUrl(referral) {
        const redirectUrl = this.isMobile ? `https://www.wix.com/website/builder#!/intro/start` : `https://www.wix.com/new/vertical`;
        // eslint-disable-next-line camelcase
        const query = queryString.stringify({ additional_referral_info: referral });
        return `${redirectUrl}?${query}`;
    }
    getLogoShareUrl(userId, logoKey) {
        return this.createUrl(joinUrl(routes.SHARE, userId, logoKey + ''), {});
    }
    getPartnersPageUrl(source) {
        const arenaNavigationParam = ArenaNavigationParam[source];
        const redirectUrl = `${ARENA_BASED_URL}?${new URLSearchParams(arenaNavigationParam).toString()}`;
        return this.inBizMgr ? redirectUrl : `${this._getBaseMount()}${routes.PARTNERS}?redirectUrl=${encodeURIComponent(redirectUrl)}`;
    }
}
//# sourceMappingURL=LogoUrlGenerator.js.map